import React, { useEffect, useMemo } from "react";
import {
  CheckCircle,
  Zap,
  ShieldCheck,
  Users,
  Sparkles,
  Headphones
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPricingCards from "../components/MarketingPricingCards";
import MarketingFAQ from "../components/MarketingFAQ";
import { marketingNavLinks } from "../constants/marketingNav";

const comparisons = [
  { feature: "Projects", starter: "5", pro: "Unlimited", enterprise: "Unlimited" },
  { feature: "Team Members", starter: "10", pro: "Unlimited", enterprise: "Unlimited" },
  { feature: "Kanban Boards", starter: "check", pro: "check", enterprise: "check" },
  { feature: "Credentials Vault", starter: "cross", pro: "check", enterprise: "check" },
  { feature: "AI Extraction", starter: "cross", pro: "check", enterprise: "check" },
  { feature: "Real-time Sync", starter: "check", pro: "check", enterprise: "check" },
  { feature: "Custom Roles", starter: "cross", pro: "check", enterprise: "check" },
  { feature: "Audit Logs", starter: "cross", pro: "Basic", enterprise: "Advanced" },
  { feature: "SSO/SAML", starter: "cross", pro: "cross", enterprise: "check" },
  { feature: "Support", starter: "Community", pro: "Priority", enterprise: "24/7 Dedicated" },
];

const PricingPage = () => {
  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Testimonials") return { ...link, route: "/testimonials" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .reveal, .landing-page .section-animate"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} ctaTarget="contact" />

      <main>
        <section className="curved-section shell-sky section-animate pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="section-eyebrow reveal">
              <Zap className="w-4 h-4 inline mr-2" />
              Transparent Pricing
            </span>
            <h1 className="reveal stagger-1 mt-6 mb-8">
              Plans for teams of <br />
              <span className="text-blue-600">all sizes</span>
            </h1>
            <p className="text-lead reveal stagger-2 max-w-2xl mx-auto">
              Simple, predictable pricing. No hidden fees, no per-seat surprises. 
              Start for free and scale as you grow.
            </p>
          </div>
        </section>

        <MarketingPricingCards className="-mt-20" />

        <section className="shell-white section-animate">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <h2 className="mb-4 text-navy">Compare Features</h2>
              <p className="text-body">Find the perfect plan for your team's needs.</p>
            </div>
            
            <div className="comparison-table-container reveal stagger-1">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th className="text-left">Features</th>
                    <th className="text-center">Starter</th>
                    <th className="text-center">Pro</th>
                    <th className="text-center">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {comparisons.map((row) => (
                    <tr key={row.feature}>
                      <td className="font-bold text-gray-900">{row.feature}</td>
                      <td className="text-center">
                        {row.starter === "check" ? <CheckCircle className="w-6 h-6 text-green-500 mx-auto" /> : 
                         row.starter === "cross" ? <span className="text-gray-300">-</span> : 
                         <span className="text-sm font-bold text-gray-600">{row.starter}</span>}
                      </td>
                      <td className="text-center bg-blue-50/30">
                        {row.pro === "check" ? <CheckCircle className="w-6 h-6 text-blue-600 mx-auto" /> : 
                         <span className="text-sm font-black text-blue-600">{row.pro}</span>}
                      </td>
                      <td className="text-center">
                        {row.enterprise === "check" ? <CheckCircle className="w-6 h-6 text-navy mx-auto" /> : 
                         <span className="text-sm font-bold text-gray-900">{row.enterprise}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="shell-navy text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-12">
              {[
                { icon: ShieldCheck, title: "Enterprise Security", desc: "Bank-grade encryption and security protocols." },
                { icon: Users, title: "Unlimited Users", desc: "Grow your team without worrying about per-seat costs." },
                { icon: Sparkles, title: "AI-Powered", desc: "Save hours with Gemini-powered ticket extraction." },
                { icon: Headphones, title: "Expert Support", desc: "Our team is here to help you 24/7." }
              ].map((item, idx) => (
                <div key={idx} className="text-center reveal stagger-1">
                  <div className="w-16 h-16 bg-white/10 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-8 h-8 text-sky" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{item.title}</h3>
                  <p className="text-blue-100 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <MarketingFAQ />

        <section id="cta" className="shell-sky text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 text-navy">Still have questions?</h2>
            <p className="text-lead mb-12">
              Our team is happy to help you find the right plan for your business.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="btn-primary">
                Contact Sales
              </button>
              <button className="btn-secondary">
                Schedule a Demo
              </button>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default PricingPage;
